import { useEffect, useState } from "react";
import { parseISO } from "date-fns";
import {
    AccountingReportCycle,
    AccountingReportCycleOptionType,
} from "@common/constants";
import { isEqual } from "@common/helpers/comparison";
import { DateRange } from "@common/helpers/date";
import { Task } from "@common/types/task";
import {
    readReportComparisonOptions,
    ReportComparisonOptions,
} from "@components/reports/reportDisplayFilters";

interface TaskReportFilterValues {
    periodStart?: string;
    periodEnd?: string;
    cycle?: AccountingReportCycleOptionType;
}

function getTaskReportFilters({
    periodStart,
    periodEnd,
    cycle,
}: TaskReportFilterValues) {
    return {
        dateRange: {
            start: periodStart ? parseISO(periodStart) : undefined,
            end: periodEnd ? parseISO(periodEnd) : undefined,
        },
        cycle: cycle ?? AccountingReportCycle.MONTH,
    };
}

export function useTaskReportFilters(selectedTask: Task) {
    const savedFilters = selectedTask.view?.filters;
    const periodStart = savedFilters?.periodStart;
    const periodEnd = savedFilters?.periodEnd;
    const cycle = savedFilters?.cycle;
    const savedComparisonOptions = readReportComparisonOptions(savedFilters);

    const [localDateRange, setLocalDateRange] = useState<Partial<DateRange>>(
        () => getTaskReportFilters({ periodStart, periodEnd, cycle }).dateRange,
    );

    const [localCycle, setLocalCycle] =
        useState<AccountingReportCycleOptionType>(
            () => getTaskReportFilters({ periodStart, periodEnd, cycle }).cycle,
        );

    const [comparisonOptions, setComparisonOptions] =
        useState<ReportComparisonOptions>(savedComparisonOptions);
    const [syncedComparisonOptions, setSyncedComparisonOptions] =
        useState<ReportComparisonOptions>(savedComparisonOptions);

    if (!isEqual(syncedComparisonOptions, savedComparisonOptions)) {
        setSyncedComparisonOptions(savedComparisonOptions);
        setComparisonOptions(savedComparisonOptions);
    }

    useEffect(() => {
        const taskReportFilters = getTaskReportFilters({
            periodStart,
            periodEnd,
            cycle,
        });

        setLocalDateRange(taskReportFilters.dateRange);
        setLocalCycle(taskReportFilters.cycle);
    }, [selectedTask.id, periodStart, periodEnd, cycle]);

    return {
        localDateRange,
        setLocalDateRange,
        localCycle,
        setLocalCycle,
        comparisonOptions,
        setComparisonOptions,
    };
}
